import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  input,
  OnInit,
  output,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { MatTree } from '@angular/material/tree';

import { AppSharedModule } from 'app/app-shared.module';

import {
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';

export interface TreePickerItem {
  id: string;
  title: string;
  options?: TreePickerItem[];
}

export interface TreePickerFlatItem {
  id: string;
  title: string;
  level: number;
  isExpanded?: boolean;
  isExpandable?: boolean;
  visible?: boolean;
}

@Component({
  selector: 'app-lex-xi-tree-picker',
  imports: [AppSharedModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './lex-xi-tree-picker.component.html',
  styleUrl: './lex-xi-tree-picker.component.scss',
})
export class LexXiTreePickerComponent implements OnInit {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();

  @ViewChild('tree') tree!: MatTree<TreePickerItem>;

  items = input<TreePickerItem[]>([]);

  onItemsChanged = output<string[]>();

  selectedNode: WritableSignal<TreePickerItem> = signal<TreePickerItem>(null);
  searchValue: WritableSignal<string> = signal<string>('');

  datasource: TreePickerFlatItem[] = [];

  readonly levelAccessor = (node: TreePickerFlatItem): number => node.level;
  readonly isExpandable = (_: number, node: TreePickerFlatItem): boolean => node.isExpandable;

  ngOnInit(): void {
    this.datasource = this.buildFlatNodes(this.items(), 0);
  }

  private buildFlatNodes(
    nodes: TreePickerItem[],
    level: number,
    isExpanded = false
  ): TreePickerFlatItem[] {
    const result: TreePickerFlatItem[] = [];
    for (const n of nodes) {
      const hasChildren = !!n.options?.length;
      const node: TreePickerFlatItem = {
        id: n.id,
        title: n.title,
        level,
        isExpanded: isExpanded,
        isExpandable: hasChildren,
        visible: hasChildren || isExpanded,
      };
      result.push(node);
      if (n.options) {
        result.push(...this.buildFlatNodes(n.options, level + 1, isExpanded));
      }
    }
    return result;
  }

  applyFilter(filterText: string) {
    this.searchValue.set(filterText);

    this.datasource = this.filterRecursive(filterText, 'title');

    if (filterText) {
      this.datasource.forEach((n) => {
        if (n.isExpandable) {
          n.isExpanded = true;
        }
      });
    } else {
      this.datasource.forEach((n) => (n.isExpanded = false));
    }
  }

  filterRecursive(filterText: string, property: keyof TreePickerItem): TreePickerFlatItem[] {
    if (filterText) {
      const filteredTree = this.filterNested(filterText, this.items(), property);
      return this.buildFlatNodes(filteredTree, 0, true);
    }
    return this.buildFlatNodes(this.items(), 0);
  }

  private filterNested(
    filterText: string,
    nodes: TreePickerItem[],
    prop: keyof TreePickerItem
  ): TreePickerItem[] {
    const lower = filterText.toLowerCase();
    const result: TreePickerItem[] = [];
    for (const node of nodes) {
      const clone: TreePickerItem = { id: node.id, title: node.title };
      if (node.options) {
        const filteredChildren = this.filterNested(filterText, node.options, prop);
        if (filteredChildren.length) {
          clone.options = filteredChildren;
        }
      }
      if (node.title.toLowerCase().includes(lower) || clone.options?.length) {
        result.push(clone);
      }
    }
    return result;
  }

  onExpandedChange(node: TreePickerFlatItem, isExpanded: boolean): void {
    node.isExpanded = isExpanded;
    // Expand/hide all children
    this.getChildren(node).forEach((n) => (n.visible = isExpanded));
  }

  getChildren(parentNode: TreePickerFlatItem): TreePickerFlatItem[] {
    const startIndex = this.datasource.indexOf(parentNode);
    const endIndex = this.datasource.findIndex(
      (item, i) => i > startIndex && item.level !== parentNode.level + 1
    );
    return this.datasource.slice(
      startIndex + 1,
      endIndex === -1 ? this.datasource.length : endIndex
    );
  }

  selectNode(node: TreePickerItem): void {
    this.selectedNode.set(node);
    this.onItemsChanged.emit([this.selectedNode().id]);
  }
}
